import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

// Services
import { userService } from '../../services/users';
import { querySearch } from '../../services/search';

// Interfaces
import type { Album } from '../../interfaces/albums';
import type { Artist } from '../../interfaces/artist';
import type { Playlist } from '../../interfaces/playlists';
import type { Track, TrackWithSave } from '../../interfaces/track';
import { RootState } from '../store';

type Item = Playlist | Album | Track | Artist;

export type SearchSection = 'ALL' | 'ARTISTS' | 'TRACKS' | 'ALBUMS' | 'PLAYLISTS';

const initialState: {
  top: Item | null;
  songs: TrackWithSave[];
  artists: Artist[];
  albums: Album[];
  playlists: Playlist[];
  loading: boolean;
  section: SearchSection;
  songsTotal: number;
  lastQuery: string;
} = {
  playlists: [],
  songs: [],
  artists: [],
  albums: [],
  top: null,
  loading: true,
  section: 'ALL',
  songsTotal: 0,
  lastQuery: '',
};

const fetchArtists = createAsyncThunk<Artist[], string>('search/fetchArtists', async (query) => {
  const response = await querySearch({ q: query, type: 'artist', limit: 10 });
  return response.data.artists.items;
});

const fetchAlbums = createAsyncThunk<Album[], string>('search/fetchAlbums', async (query) => {
  const response = await querySearch({ q: query, type: 'album', limit: 10 });
  return response.data.albums.items;
});

const fetchPlaylists = createAsyncThunk<Playlist[], string>(
  'search/fetchPlaylists',
  async (query) => {
    const response = await querySearch({ q: query, type: 'playlist', limit: 10 });
    return response.data.playlists.items;
  }
);

const fetchSongs = createAsyncThunk<[TrackWithSave[], number], string>(
  'search/fetchSongs',
  async (query, params) => {
    const response = await querySearch({ q: query, type: 'track', limit: 30 });
    const tracks = response.data.tracks.items;
    const total = response.data.tracks.total;

    const extraRequests = [
      userService.checkSavedTracks(tracks.map((t: any) => t.id)).catch(() => ({
        data: [],
      })),
    ];

    await Promise.all(extraRequests);

    const saves = (await extraRequests[0]).data;

    const items = tracks.map((track: any, index: number) => ({
      ...track,
      saved: saves[index],
    }));

    return [items, total];
  }
);

const fetchMoreSongs = createAsyncThunk<TrackWithSave[], string>(
  'search/fetchMoreSongs',
  async (query, { getState }) => {
    const state = getState() as RootState;
    const { songs } = state.search;

    const response = await querySearch({
      q: query,
      limit: 10,
      type: 'track',
      offset: songs.length,
    });
    const tracks = response.data.tracks.items;

    const extraRequests = [
      userService.checkSavedTracks(tracks.map((t: any) => t.id)).catch(() => ({
        data: [],
      })),
    ];

    await Promise.all(extraRequests);

    const saves = (await extraRequests[0]).data;

    return tracks.map((track: any, index: number) => ({
      ...track,
      saved: saves[index],
    }));
  }
);

const searchMemoryCache = new Map<
  string,
  {
    timestamp: number;
    data: [Item, [TrackWithSave[], number], Artist[], Album[], Playlist[]];
  }
>();

export const fetchSearch = createAsyncThunk<
  { query: string; data: [Item, [TrackWithSave[], number], Artist[], Album[], Playlist[]] },
  string
>('search/fetchSearch', async (query) => {
  const normalized = query.trim().toLowerCase();

  // Instant in-memory cache hit (valid for 5 minutes)
  const cached = searchMemoryCache.get(normalized);
  if (cached && Date.now() - cached.timestamp < 300000) {
    return { query: normalized, data: cached.data };
  }

  const promises = [
    querySearch({ q: query, type: 'track', limit: 25 }),
    querySearch({ q: query, type: 'album', limit: 10 }),
    querySearch({ q: query, type: 'artist', limit: 10 }),
    querySearch({ q: query, type: 'playlist', limit: 10 }),
  ];

  const responses = await Promise.all(promises);

  const tracks = responses[0].data.tracks.items;
  const tracksTotal = responses[0].data.tracks.total;
  const albums = responses[1].data.albums.items;
  const artists = responses[2].data.artists.items;
  const playlists = responses[3].data.playlists.items;

  const topItems = [artists[0], albums[0], tracks[0], playlists[0]];

  const topItem =
    topItems.find((item) => item?.name?.toLowerCase() === query?.toLowerCase()) ||
    topItems.find((item) => item?.name?.toLowerCase().includes(query?.toLowerCase())) ||
    topItems.find((item) => !!item) ||
    tracks[0] ||
    null;

  const extraRequests = [
    userService.checkSavedTracks(tracks.map((t: any) => t.id)).catch(() => ({
      data: [],
    })),
  ];

  await Promise.all(extraRequests);

  const saves = (await extraRequests[0]).data;

  const tracksWithSaves: TrackWithSave[] = tracks.map((track: any, index: number) => ({
    ...track,
    saved: saves[index],
  }));

  const payloadData: [Item, [TrackWithSave[], number], Artist[], Album[], Playlist[]] = [
    topItem,
    [tracksWithSaves, tracksTotal],
    artists,
    albums,
    playlists,
  ];

  searchMemoryCache.set(normalized, {
    timestamp: Date.now(),
    data: payloadData,
  });

  return { query: normalized, data: payloadData };
});

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setSection(state, action: PayloadAction<SearchSection>) {
      state.section = action.payload;
    },
    setSavedStateForTrack(
      state,
      action: PayloadAction<{
        id: string;
        saved: boolean;
      }>
    ) {
      const track = state.songs.find((t) => t.id === action.payload.id);
      if (track) {
        track.saved = action.payload.saved;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSearch.pending, (state, action) => {
      state.loading = true;
      state.lastQuery = (action.meta.arg || '').trim().toLowerCase();
    });
    builder.addCase(fetchSearch.fulfilled, (state, action) => {
      // Discard stale responses if user moved on to a newer search query
      if (action.payload.query !== state.lastQuery) {
        return;
      }

      state.top = action.payload.data[0];
      state.songs = action.payload.data[1][0];
      state.songsTotal = action.payload.data[1][1];

      state.artists = action.payload.data[2];
      state.albums = action.payload.data[3];
      state.playlists = action.payload.data[4];
      state.loading = false;
    });
    builder.addCase(fetchSearch.rejected, (state, action) => {
      if ((action.meta.arg || '').trim().toLowerCase() === state.lastQuery) {
        state.loading = false;
      }
    });
    builder.addCase(fetchArtists.fulfilled, (state, action) => {
      state.artists = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchAlbums.fulfilled, (state, action) => {
      state.albums = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchPlaylists.fulfilled, (state, action) => {
      state.playlists = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchSongs.fulfilled, (state, action) => {
      state.songs = action.payload[0];
      state.songsTotal = action.payload[1];
      state.loading = false;
    });
    builder.addCase(fetchMoreSongs.fulfilled, (state, action) => {
      state.songs = [...state.songs, ...action.payload];
      state.loading = false;
    });
  },
});

export const searchActions = {
  fetchSearch,
  fetchArtists,
  fetchAlbums,
  fetchPlaylists,
  fetchMoreSongs,
  fetchSongs,
  ...searchSlice.actions,
};

export default searchSlice.reducer;
